import { getDefaultLang } from "@cervval.js/common/src/i18n/languages";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import * as Handlebars from "handlebars";

const _settings = {
    initHandlerbarsWasCalled: false
};

/**
 * register default handlebars helpers
 */
export function initHandlerbarsIfNeeded(): void {
    if (_settings.initHandlerbarsWasCalled) {
        return;
    }
    _settings.initHandlerbarsWasCalled = true;

    /**
     * register plural helper
     * 
     * template "{{pluralCount count 'element'}}" { count : 1 } => "element"
     * template "{{pluralCount count 'element'}}" { count : 2 } => "elements"
     * template "{{pluralCount count 'element' 'elementz'}}" { count : 2 } => "elementz"
     * 
     * template "{{plural count 'has' 'have'}}" { count : 0 } => "have"
     * template "{{plural count 'has' 'have'}}" { count : 1 } => "has"
     * template "{{plural count 'has' 'have'}}" { count : 2 } => "have"
     */
    Handlebars.registerHelper("plural", function (number, singular, plural) {
        if (number === 1) {
            return singular;
        } else {
            // if no plural is provided, add s after singular
            return typeof plural === "string" ? plural : singular + "s";
        }
    });

    /**
     * register pluralCount helper (same as plural but emits the number before adding plural text)
     * 
     * template "{{pluralCount count 'element'}}" { count : 1 } => "1 element"
     * template "{{pluralCount count 'element'}}" { count : 2 } => "2 elements"
     * template "{{pluralCount count 'element' 'elementz'}}" { count : 2 } => "2 elementz"
     */
    Handlebars.registerHelper("pluralCount", function (number, singular, plural) {
        return number + " " + Handlebars.helpers["plural"](number, singular, plural);
    });
}

initHandlerbarsIfNeeded();

/**
 * Generate function from precompiled handlebars template
 */
export function ghb(precompiledTemplate: TemplateSpecification): (input: unknown) => string {
    return (input: unknown) => {
        const compiledTemplate = Handlebars.template(precompiledTemplate);
        return compiledTemplate(input);
    };
}

export type HBinput = string | number;

/**
 * Display warning in console when using fallback language
 */
function _warnUsingFallbackLanguage(askedLang: string | undefined, fallbackToLang: string): void {
    if (askedLang) {
        getLogConsole().warn(askedLang + " i18n was not found, using " + fallbackToLang + " instead");
    }
}

/**
 * @returns the i18n for provided lang (fallback on app.lang if i18n not found for lang)
 */
export function i18nLoad<T>(lang: string | undefined, langs: string[], loadLangFile: (langToLoad: string) => { i18n: T }): T {
    let result: { i18n: T } | undefined;
    if (lang) {
        result = loadLangFile(lang);
    }
    if (!result && langs?.length) {
        const defaultLang = getDefaultLang();
        if (!result && defaultLang && lang !== defaultLang && langs.indexOf(defaultLang) >= 0) {
            // if langFile was not specified or not handled, try the default lang first
            _warnUsingFallbackLanguage(lang, defaultLang);
            result = loadLangFile(defaultLang);
        }

        if (!result && lang !== langs[0]) {
            // if langFile was not specified or not handled, try the first lang found
            _warnUsingFallbackLanguage(lang, langs[0]);
            result = loadLangFile(langs[0]);
        }
    }
    if (!result) {
        // always return something
        getLogConsole().error(lang + " i18n file not found");
        return {} as T;
    }
    return result.i18n;
}